import { useMatches } from 'react-router-dom';
import type { RouteHandle, LayoutOptions } from '../routes/routeTypes';

const defaultLayoutOptions: LayoutOptions = {
  hideHeader: false,
  defaultCompactSidebar: false,
};

/**
 * Hook to get layout options from the current route's handle.
 * Uses React Router's useMatches() to find the deepest route with layoutOptions.
 *
 * Usage in route config:
 * {
 *   path: '/some-page',
 *   element: <SomePage />,
 *   handle: { layoutOptions: { hideHeader: true, defaultCompactSidebar: true } }
 * }
 */
export const useRouteLayoutOptions = (): LayoutOptions => {
  const matches = useMatches();

  // Find the deepest match that has layoutOptions
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i].handle as RouteHandle | undefined;
    if (handle?.layoutOptions) {
      return { ...defaultLayoutOptions, ...handle.layoutOptions };
    }
  }

  return defaultLayoutOptions;
};

// Walks the active route matches and returns true if any of them declares
// `requiresChannel: true` in its handle. Used by DashBoardLayout to gate
// the no-channels empty-state swap.
export const useRouteRequiresChannel = (): boolean => {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i].handle as RouteHandle | undefined;
    if (handle?.requiresChannel) return true;
  }
  return false;
};
